import { useAtomValue, useStore, type PrimitiveAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';

import { useEvidenceSourceConfig } from '@/components/chat/hooks/useEvidenceSourceConfig';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { createTimelineStream } from '@/utils/timelineEventSource';
import type { Chunk, TimelineEntry } from '@heidi/evidence/contracts';
import {
  ChatMode,
  EvidenceSBSStreamStatus,
  type EvidenceSBSCandidate,
} from '@heidi/evidence/contracts';
import {
  evidenceSBSAbortControllersAtom,
  evidenceSBSCandidatesAtom,
  evidenceSBSChatIdAtom,
  evidenceSBSMessagesAtom,
  evidenceSBSSessionIdAtom,
  evidenceSBSStartedChatIdAtom,
  evidenceSBSStreamAAtom,
  evidenceSBSStreamBAtom,
  finalizeTimeline,
  makeStreamingConversation,
  processChatChunk,
  type SBSStreamState,
} from '@heidi/evidence/data';
import { generateId } from '@heidi/utils';
import { getCountryRaw } from '@heidi/utils/get-country';

export type { SBSStreamState } from '@heidi/evidence/data';

/**
 * Backend errors may arrive as `error_item` chunks; the stream still calls `onComplete`.
 * Those completions must not be treated as DONE or Evidence SBS auto-fallback will not run
 * when the other stream succeeds (EVI-1312).
 */
export function getTimelineTerminalErrorMessage(
  finalized: TimelineEntry[]
): string | null {
  for (const entry of finalized) {
    if (entry.type === 'error_item') {
      return entry.content;
    }
    if (entry.type === 'unauthenticated_tool_access_error_item') {
      return entry.message;
    }
  }
  return null;
}

/**
 * Dev-only local repro (EVI-1312 QA): in the browser console run
 * `sessionStorage.setItem('evidenceSbsDevReproErrorItem', '1')` then reload and trigger SBS.
 * Injects an `error_item` on option A (first stream) after the real stream completes.
 * With the fix, that side becomes ERROR and auto-fallback picks option B. Remove with
 * `sessionStorage.removeItem('evidenceSbsDevReproErrorItem')`.
 */
export const EVIDENCE_SBS_DEV_REPRO_ERROR_SESSION_KEY = 'evidenceSbsDevReproErrorItem';

export interface UseEvidenceSBSStreamsOptions {
  /**
   * Test-only escape hatch that disables the mounted-chat mismatch guard so
   * unit tests can drive the atoms directly without wiring a matching
   * `mountedChatId`. Never set this in production code.
   */
  disableChatMismatchGuard?: boolean;
}

/**
 * @param mountedChatId The chat id of the chat this hook instance is mounted
 *   under. Streams only auto-start while the SBS experiment in the atoms
 *   (`evidenceSBSChatIdAtom`) belongs to this same chat — a mounted chat that
 *   does not equal the experiment's chat, including a `null`/`undefined`
 *   "no chat id yet", blocks auto-start so a previous chat's candidates can't
 *   leak `chat/completions` requests after the user navigates away (EVI-1680,
 *   bug B). In-flight streams, however, are aborted ONLY on a *resolved*
 *   cross-chat navigation (a non-null mounted id that differs from the
 *   experiment's chat); a `null`/`undefined` mounted id is treated as an
 *   unresolved/transient remount and leaves the persisted streams running, so
 *   switching tabs and back never cancels the in-flight answer (EVI-1701).
 * @param options.disableChatMismatchGuard Test-only opt-out of the guard. See
 *   {@link UseEvidenceSBSStreamsOptions}.
 */
export function useEvidenceSBSStreams(
  mountedChatId?: string | null,
  options?: UseEvidenceSBSStreamsOptions
) {
  const candidates = useAtomValue(evidenceSBSCandidatesAtom);
  const messages = useAtomValue(evidenceSBSMessagesAtom);
  const chatId = useAtomValue(evidenceSBSChatIdAtom);
  const sessionId = useAtomValue(evidenceSBSSessionIdAtom);

  // Stream state lives in Jotai (see atoms.ts) so the in-flight streams survive
  // the Evidence panel unmounting on a tab switch — read reactively here, but
  // written imperatively from the SSE callbacks via `store` so updates still
  // land while this component is unmounted.
  const streamA = useAtomValue(evidenceSBSStreamAAtom);
  const streamB = useAtomValue(evidenceSBSStreamBAtom);

  const store = useStore();

  // The mounted chat can differ from the chat the atom-held experiment belongs
  // to. We use that ONLY to gate auto-start — never to abort.
  //
  // `shouldBlockStreamStart` keeps a stale/leaked experiment from firing fresh
  // `chat/completions` for a chat the user navigated away from (EVI-1680, bug B);
  // a `null`/`undefined` "no chat id yet" counts here too. But in-flight streams
  // are NEVER torn down on a mismatch — that abort is exactly what cancelled the
  // answer when the panel unmounts/remounts on a tab switch (a sessionless or
  // eager-created Evidence chat remounts with a not-yet-resolved id for a render
  // or two). Streams persist in Jotai and the overlay simply hides itself when
  // the mounted chat doesn't match; teardown happens only through
  // `resetEvidenceSBSAction` (dismiss / skip / confirm / new round). Tests opt
  // out of the start-gate via `disableChatMismatchGuard`.
  const shouldBlockStreamStart =
    !options?.disableChatMismatchGuard && mountedChatId !== chatId;

  const {
    effectiveDomains,
    includeUserDocs,
    includeWebSearch,
    sourceListIds,
    userDocCollectionIds,
    integrationPartners,
    evidenceSourceFilters,
  } = useEvidenceSourceConfig();

  const { data: userData } = useQueryUser();

  const evidenceVersion = 3;
  // Keep-ON release-evidence-auto-mode: SBS always streams in AUTO.
  const effectiveMode = ChatMode.AUTO;

  // Read inside the stable startStream callback without making it reactive.
  const shouldBlockStreamStartRef = useRef(shouldBlockStreamStart);
  shouldBlockStreamStartRef.current = shouldBlockStreamStart;

  // Store latest stream config in a ref so startStream doesn't need
  // reactive deps that would cause useEffect re-runs.
  const streamConfigRef = useRef({
    chatId,
    messages,
    sessionId,
    effectiveMode,
    evidenceVersion,
    effectiveDomains,
    includeUserDocs,
    includeWebSearch,
    evidenceSourceFilters,
    sourceListIds,
    userDocCollectionIds,
    integrationPartners,
    userData,
  });
  streamConfigRef.current = {
    chatId,
    messages,
    sessionId,
    effectiveMode,
    evidenceVersion,
    effectiveDomains,
    includeUserDocs,
    includeWebSearch,
    evidenceSourceFilters,
    sourceListIds,
    userDocCollectionIds,
    integrationPartners,
    userData,
  };

  // Stable function ref — never changes identity, reads config from ref and
  // writes stream state through `store` (not React state) so the SSE callbacks
  // keep updating the atom even after the panel unmounts.
  const startStream = useCallback(
    (
      candidate: EvidenceSBSCandidate,
      streamAtom: PrimitiveAtom<SBSStreamState>,
      streamIndex: 0 | 1
    ) => {
      const config = streamConfigRef.current;
      if (!config.chatId || config.messages.length === 0) return;
      // Defensive: never stream for a chat other than the one mounted.
      if (shouldBlockStreamStartRef.current) return;

      const abortController = new AbortController();
      store.set(evidenceSBSAbortControllersAtom, (prev) => [...prev, abortController]);

      store.set(streamAtom, {
        conversation: makeStreamingConversation(),
        isStreaming: true,
        status: EvidenceSBSStreamStatus.STREAMING,
        error: null,
      });

      createTimelineStream({
        messages: config.messages,
        sessionId: config.sessionId,
        chatId: config.chatId,
        candidateId: candidate.candidate_id,
        mode: config.effectiveMode,
        version: config.evidenceVersion,
        domains: config.effectiveDomains,
        includeUserDocs: config.includeUserDocs,
        includeWebSearch: config.includeWebSearch,
        evidenceSourceFilters: config.evidenceSourceFilters,
        sourceListIds: config.sourceListIds,
        userDocCollectionIds: config.userDocCollectionIds,
        integrationPartners: config.integrationPartners,
        isInSession: !!config.sessionId,
        country: !config.userData ? (getCountryRaw() ?? undefined) : undefined,
        onChunk: (chunk: Chunk) => {
          if (abortController.signal.aborted) return;
          store.set(streamAtom, (prev) => {
            const result = processChatChunk(
              chunk,
              prev.conversation,
              config.effectiveMode
            );
            if (!result) return prev;
            return {
              ...prev,
              conversation: {
                ...prev.conversation,
                timeline: result.timeline,
                streamingState: result.streamingState,
              },
            };
          });
        },
        onComplete: () => {
          if (abortController.signal.aborted) return;
          store.set(streamAtom, (prev) => {
            let finalized = finalizeTimeline(prev.conversation.timeline);

            if (
              process.env.NODE_ENV === 'development' &&
              typeof window !== 'undefined' &&
              window.sessionStorage?.getItem(EVIDENCE_SBS_DEV_REPRO_ERROR_SESSION_KEY) ===
                '1' &&
              streamIndex === 0
            ) {
              finalized = [
                ...finalized,
                {
                  type: 'error_item' as const,
                  id: generateId(),
                  content: 'An unexpected error occurred. Please try again.',
                  position: finalized.length,
                },
              ];
            }

            const timelineErrorMessage = getTimelineTerminalErrorMessage(finalized);
            const hasRealContent =
              finalized.length > 0 &&
              finalized.some((entry) => entry.type !== 'reasoning_item');
            const success = Boolean(hasRealContent && !timelineErrorMessage);

            return {
              ...prev,
              isStreaming: false,
              status: success
                ? EvidenceSBSStreamStatus.DONE
                : EvidenceSBSStreamStatus.ERROR,
              error: success ? null : (timelineErrorMessage ?? prev.error),
              conversation: {
                ...prev.conversation,
                timeline: finalized,
                streamingState: null,
              },
            };
          });
        },
        onError: (err) => {
          if (abortController.signal.aborted) return;
          store.set(streamAtom, (prev) => ({
            ...prev,
            isStreaming: false,
            status: EvidenceSBSStreamStatus.ERROR,
            error: err.message,
            conversation: {
              ...prev.conversation,
              timeline: finalizeTimeline(prev.conversation.timeline),
              streamingState: null,
            },
          }));
        },
        abortController,
      }).catch(() => {
        // fetchEventSource may throw after onError
      });
    },
    [store]
  );

  // Start streams when candidates are ready. Guarded by `evidenceSBSStartedChatIdAtom`
  // (persisted, not a component ref) so the streams fire exactly once per round
  // — and crucially are NOT restarted when the panel remounts after a tab
  // switch, since the guard sees they're already running for this chat. A new
  // round always runs through `resetEvidenceSBSAction` first, which clears the
  // guard back to null. `shouldBlockStreamStart` blocks auto-start for a leaked
  // experiment that belongs to a chat the user navigated away from (EVI-1680).
  useEffect(() => {
    if (
      candidates.length < 2 ||
      messages.length === 0 ||
      !chatId ||
      shouldBlockStreamStart
    )
      return;
    if (store.get(evidenceSBSStartedChatIdAtom) === chatId) return;
    store.set(evidenceSBSStartedChatIdAtom, chatId);

    startStream(candidates[0]!, evidenceSBSStreamAAtom, 0);
    startStream(candidates[1]!, evidenceSBSStreamBAtom, 1);
  }, [candidates, messages, chatId, shouldBlockStreamStart, startStream, store]);

  // NOTE: there is deliberately NO effect that aborts the streams on a chat
  // mismatch or on unmount. EVI-1680 originally aborted in-flight streams when
  // `mountedChatId` diverged from the experiment's chat, and EVI-1701 aborted on
  // unmount — but the Evidence panel fully unmounts (and remounts with a
  // not-yet-resolved chat id) when the user switches to another tab (Scribe,
  // etc.) and back, so both abort the in-flight answer the moment the panel
  // returns. A leaked experiment can no longer *start* fresh streams thanks to
  // `shouldBlockStreamStart`; existing streams just finish in the background and
  // the overlay hides itself when the mounted chat doesn't match. Teardown
  // happens only through `resetEvidenceSBSAction` (dismiss / skip / confirm /
  // new round), mirroring how normal Evidence Q&A lets its single stream finish.

  return { streamA, streamB, candidates };
}
